import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { getPreferredClient } from '@/lib/auth';
import { getActiveAnnouncements } from '../config/announcements';
import { buildDismissPreferenceKey } from '../keys';
import type { AnnouncementDisplayType } from '../types';

/**
 * Auto-dismisses announcements when the user visits the configured `dismissOnVisit` route.
 * Persists dismissals for both modal and banner display types so the announcement
 * won't reappear in either form.
 *
 * Render this once at a top-level layout (e.g. alongside the router outlet).
 */
export function AnnouncementAutoDismiss() {
  const { pathname } = useLocation();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const dismissedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user?.id) return;

    const announcements = getActiveAnnouncements().filter(
      (a) => a.dismissOnVisit && pathname.startsWith(a.dismissOnVisit)
    );

    for (const announcement of announcements) {
      // Only attempt once per announcement per session to avoid repeated API calls
      if (dismissedRef.current.has(announcement.id)) continue;
      dismissedRef.current.add(announcement.id);

      const displayTypes: AnnouncementDisplayType[] = [];
      if (announcement.showModal) displayTypes.push('modal');
      if (announcement.showBanner) displayTypes.push('banner');

      for (const displayType of displayTypes) {
        const key = buildDismissPreferenceKey(announcement.id, displayType);

        // Optimistically update the query cache so the UI reacts immediately
        queryClient.setQueryData(['userPreference', key], true);

        getPreferredClient()
          .post('/api/UserPreferences', { userId: user.id, key, value: 'true' })
          .catch((error) => {
            console.error('[Announcements] Failed to auto-dismiss on visit:', error);
          });
      }
    }
  }, [pathname, user?.id, queryClient]);

  return null;
}
